import { useCallback } from "react";

import { ceil } from "~/tools/ceil";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";
import {
	type Api,
	type LogsDirection,
	getKubernetesLogsAutocompleteFn,
	getKubernetesLogsFn,
	getKubernetesLogsHistogramFn,
} from "../common/openapi";

import {
	type UseInfiniteQueryOptions,
	type UseInfiniteQueryResult,
	type UseQueryOptions,
	type UseQueryResult,
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "./react-query";

const logsPageSize = 50;

export interface useQueryPodContainerNamesOptions {
	cluster: string;
	podUID: string;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const useQueryPodContainerNames = <TResult = Array<string>>(
	{ cluster, podUID, timestamp, timeRange }: useQueryPodContainerNamesOptions,
	options?: UseQueryOptions<Array<string>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["pod", "logs", "containerNames", cluster, podUID],
		async () => {
			const now = Date.now();
			const { containerNames } = await getKubernetesLogsAutocompleteFn({
				cluster,
				topologyTime: getTime(timestamp),
				aFrom: getTime(timeRange.start(now)),
				to: getTime(timeRange.endDate(now)),
				podUID,
			});

			return containerNames;
		},
		{ ...options, timeRange },
	);

export type PodLogsResult = Api.GetKubernetesLogsResult;

export const useInfinitiveQueryPodLogs = <TResult = PodLogsResult>(
	{
		cluster,
		podUid,
		query,
		direction,
		containerNames,
		timestamp,
		timeRange,
		severityList,
	}: {
		cluster: string;
		podUid: string;
		direction: LogsDirection;
		query?: string;
		containerNames?: Array<string>;
		timestamp: undefined | Time;
		timeRange: TimeRange;
		severityList?: Array<Api.LogSeverity>;
	},
	options?: UseInfiniteQueryOptions<number, PodLogsResult, Error, TResult>,
): UseInfiniteQueryResult<TResult, Error> => {
	return useInfiniteQuery<PodLogsResult, Error, number, TResult>({
		queryKey: ["pod", "logs", cluster, podUid, direction, containerNames?.toString(), severityList?.toString(), query], // adding sorting state as key causes table to reset and fetch from new beginning upon sort
		queryFn: async ({ pageParam = 1 }) =>
			getKubernetesLogsFn({
				cluster,
				podUID: podUid,
				topologyTime: getTime(timestamp),
				aFrom: timeRange.start().valueOf(),
				to: timeRange.endDate().valueOf(),
				page: pageParam,
				pageSize: logsPageSize,
				query,
				direction,
				containerNames,
				severity: severityList,
			}),
		getNextPageParam: (lastPage) => {
			const nextPage = lastPage.page + 1;
			return lastPage.page < ceil(lastPage.matchesTotal / lastPage.pageSize, 0) ? nextPage : undefined;
		},
		refetchOnWindowFocus: false,
		...options,
		timeRange,
	});
};

export const useRemoveQueriesPodLogs = ({
	cluster,
	podUid,
	direction,
	containerNames,
	severityList,
	query,
}: {
	cluster: string;
	podUid: string;
	direction: LogsDirection;
	containerNames?: string;
	severityList?: string;
	query?: string;
}) => {
	const queryClient = useQueryClient();

	return useCallback(
		() =>
			queryClient.removeQueries({
				queryKey: [{}, "pod", "logs", cluster, podUid, direction, containerNames, severityList, query],
			}),
		[queryClient, cluster, podUid, direction, containerNames, severityList, query],
	);
};

export const useQueryPodLogsHistogram = <TResult = Array<Api.KubernetesLogHistogramBucket>>(
	{
		cluster,
		podUID,
		timestamp,
		timeRange,
		query,
		containerNames,
		bucketsCount,
		severityList,
	}: {
		cluster: string;
		podUID: string;
		timestamp: undefined | Time;
		timeRange: TimeRange;
		query?: string;
		containerNames?: Array<string>;
		bucketsCount: number;
		severityList?: Array<Api.LogSeverity>;
	},
	{ enabled = true, ...options }: UseQueryOptions<Array<Api.KubernetesLogHistogramBucket>, Error, TResult> = {},
): UseQueryResult<TResult, Error> =>
	useQuery(
		[
			"pod",
			"logs",
			"histogram",
			cluster,
			podUID,
			query,
			containerNames?.toString(),
			severityList?.toString(),
			bucketsCount,
		],
		async () => {
			const { buckets } = await getKubernetesLogsHistogramFn({
				topologyTime: getTime(timestamp),
				aFrom: timeRange.start().valueOf(),
				to: timeRange.endDate().valueOf(),
				query,
				podUID,
				cluster,
				containerNames,
				bucketsCount,
				severity: severityList,
			});

			return buckets;
		},
		{
			refetchOnWindowFocus: false,
			enabled: enabled && Boolean(podUID),
			...options,
			timeRange,
		},
	);
